import * as React from 'react';

import {Routing} from 'nutshell-core/routing';
import type {FilterObject, ComparisonRangeType} from 'nutshell-core/types';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
// @ts-expect-error - imported from .js/.jsx file
import {DownloadIcon} from 'shells/icon/svg/download-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {HtmlLinkIcon} from 'shells/icon/svg/html-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {RetryRegularIcon} from 'shells/icon/svg/retry-regular-icon';
import {PaginatedTable} from 'shells/table/paginated-table';
import {convertUrlSortParamToTableSortBy} from 'shells/table/table-sort-helpers';

import {useGetInstanceInfo} from '../../../core/use-get-instance-info';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {downloadQuote} from '../../../lead/sales-documents/shared';
import {useRestoreSalesDocument} from '../../../lead/sales-documents/shared/graphql/hooks';
import {QuotesReportChart} from '../../../reports/quotes/quotes-report-chart';
import {getReportDateRanges} from '../../../reports/report-constants';
import {ReportDateRangePicker} from '../../../reports/report-date-range-picker';
import {INVOICES} from '../../../reports/report-types';

import {appHistory} from '../../../../history';

import {
    getReportFiltersFromUrlParams,
    getReportParamsFromUrlParams,
    getTableColumns,
} from '../shared/helper';
import {QuoteFilters} from '../shared/quote-filters';
import {SalesDocumentGraphCard} from '../shared/sales-document-graph-card';
import {SalesDocumentOverviewEmptyState} from '../shared/sales-document-overview-empty-state';

import {useGetFilteredInvoices} from './graphql/hooks';

import styles from '../quotes/quote-manage-page.module.css';

const NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID = 'nutshell-quote-script-container';

const SortOrder = {
    MODIFIED_TIME_DESC: '-modifiedTime',
    MODIFIED_TIME_ASC: 'modifiedTime',
    VALUE_ASC: 'value',
    VALUE_DESC: '-value',
} as const;

export function InvoiceOverviewPage() {
    const urlParamString = window.location.search.slice(1); // Remove "?"
    const urlParams = Routing.deparam(urlParamString);
    const sort = urlParams.invoices ? urlParams.invoices.sort : null;

    const [activeFilters, setActiveFilters] = React.useState<FilterObject[]>([
        {
            owner: [],
            status: null,
        },
    ]);

    const scriptRef = React.useRef<HTMLElement | null | undefined>(null);
    const instanceIdRef = React.useRef<string | null | undefined>(null);

    const {sessionInstance} = useGetInstanceInfo();
    const {restoreSalesDocument} = useRestoreSalesDocument();
    const {user} = useGetSessionUser();
    const {
        invoices,
        numInvoices,
        isLoading,
        error,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        fetchFirstPageWithNewSort,
    } = useGetFilteredInvoices({
        // @ts-expect-error
        userIds: activeFilters[0].owner || [],
        // @ts-expect-error
        status: activeFilters[0].status,
        sort,
    });

    const hasAnyFilters = Boolean(
        // @ts-expect-error
        activeFilters[0].status || (activeFilters[0].owner && activeFilters[0].owner.label)
    );

    const handleInvoiceRestore = (invoiceId: string) => {
        restoreSalesDocument({
            salesDocumentId: invoiceId,
        }).then((response) => {
            refetch();
            if (
                response.data &&
                response.data.salesDocumentRestore &&
                response.data.salesDocumentRestore.salesDocument.modalUrl
            ) {
                appHistory.replace({
                    ...appHistory.location,
                    hash: response.data.salesDocumentRestore.salesDocument.modalUrl,
                    state: {modal: true},
                });
            }
        });
    };

    React.useEffect(() => {
        const handleInvoiceCreated = () => {
            refetch();
        };

        window.addEventListener('salesDocumentCreated', handleInvoiceCreated);

        return () => {
            window.removeEventListener('salesDocumentCreated', handleInvoiceCreated);
        };
    }, [refetch]);

    React.useEffect(() => {
        instanceIdRef.current = sessionInstance && sessionInstance.instanceId;
    }, [sessionInstance]);

    const handleInvoiceDownload = (slug: string) => {
        if (!instanceIdRef.current || !scriptRef.current) {
            return;
        }

        downloadQuote(
            instanceIdRef.current,
            slug,
            scriptRef.current,
            NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID
        );
    };

    const columns = React.useMemo(() => getTableColumns('Invoice'), []);

    const onParamChange = React.useCallback(
        (params: any, replace?: boolean) => {
            const newUrlParams = {...urlParams, ...params} as const;
            const newParamString = Routing.param(newUrlParams);
            const newUrl = `${window.location.pathname}?${newParamString}${window.location.hash}`;

            if (replace) {
                appHistory.replace(newUrl);
            } else {
                appHistory.push(newUrl);
            }
        },
        [urlParams]
    );

    const updateUrlParamsOnSortChange = (newSortString: string) => {
        onParamChange({invoices: {sort: newSortString}}, true);
    };

    React.useEffect(() => {
        if (!sort) {
            // Necessary to keep sort state across page reloads
            // For some reason the second render does not have our sort param in urlParams
            // so we need to set it back
            onParamChange({invoices: {sort: SortOrder.MODIFIED_TIME_DESC}}, true);
        }
        if (!sort) {
            // If no sort is provided, default to MODIFIED_TIME_DESC
            onParamChange({invoices: {sort: SortOrder.MODIFIED_TIME_DESC}}, true);
        }
    }, [sort, onParamChange]);

    const quoteRowActionMenu = (row: any) => {
        if (!row || !row.original) {
            return null;
        }
        const isArchived = row.original.displayStatus === 'ARCHIVED';
        const isRevoked = row.original.displayStatus === 'REVOKED';

        const downloadOption = {
            label: 'Download invoice',
            icon: (
                <div className='pt-4'>
                    <DownloadIcon size={18} />
                </div>
            ),
            handleSelect: () => handleInvoiceDownload(row.original.slug),
        } as const;

        const options = [];
        if (!isArchived && !isRevoked) {
            options.push(
                {
                    label: 'View invoice',
                    icon: (
                        <div className='pt-4'>
                            <EyeIcon size={18} />
                        </div>
                    ),
                    handleSelect: () =>
                        window.open(row.original.shareableViewInBrowserLink, '_blank'),
                },
                {
                    label: 'Copy link to invoice',
                    icon: <HtmlLinkIcon size={18} />,
                    handleSelect: () =>
                        navigator.clipboard.writeText(row.original.shareableViewInBrowserLink),
                },
                downloadOption
            );
        } else if (isArchived) {
            options.push(downloadOption, {
                label: 'Restore invoice',
                icon: (
                    <div className='pt-4'>
                        <RetryRegularIcon size={18} />
                    </div>
                ),
                handleSelect: () => handleInvoiceRestore(row.original.id),
            });
        } else if (isRevoked) {
            options.push(downloadOption);
        }

        return (
            <div className='px-4'>
                <ActionMenu padding='less' hasHoverBackground={true} options={options} />
            </div>
        );
    };

    const onParamPush = (params: any, replace?: boolean) => {
        onParamChange(params, replace);
    };

    const reportParams = React.useMemo(() => getReportParamsFromUrlParams(urlParams), [urlParams]);

    const reportFilters = React.useMemo(
        () => getReportFiltersFromUrlParams(urlParams),
        [urlParams]
    );

    const handleDateRangeChange = (
        newDateFilter: FilterObject,
        comparisonType?: ComparisonRangeType
    ) => {
        const newParams = {filter: newDateFilter, report: {compareTo: comparisonType}} as const;
        onParamChange(newParams, true);
    };

    return (
        <div className='full-height flex flex-dir-col'>
            {NutClientConfig.hasSalesDocumentGraphsFF && (
                <>
                    <div className='flex justify-end pt-16 pr-16'>
                        <ReportDateRangePicker
                            activeFilters={reportFilters}
                            dateRangeType='period'
                            // @ts-expect-error
                            dateRangeOptions={getReportDateRanges(INVOICES)}
                            comparisonType={undefined}
                            onDateRangeChange={handleDateRangeChange}
                            hideCompareTo={true}
                        />
                    </div>
                    <SalesDocumentGraphCard
                        salesDocumentType='invoice'
                        chart={
                            <QuotesReportChart
                                salesDocumentType='invoice'
                                activeFilters={reportFilters}
                                onParamChange={onParamChange}
                                onParamPush={onParamPush}
                                reportParams={reportParams}
                            />
                        }
                    />
                </>
            )}
            <div className={styles.table}>
                <ListPageControlBar
                    leftSection={
                        <QuoteFilters
                            activeFilters={activeFilters}
                            onUpdateFilters={(filter: FilterObject) => {
                                setActiveFilters([filter]);
                            }}
                            isAdmin={user && user.permissions.canAccessBilling}
                            variant='invoice'
                        />
                    }
                />
                <PaginatedTable
                    key={`invoices-${JSON.stringify(activeFilters)}`}
                    columns={columns}
                    data={invoices || []}
                    isLoading={isLoading}
                    fullWidth={true}
                    fetchNextPage={fetchNextPage}
                    fetchPreviousPage={fetchPreviousPage}
                    pageSize={50}
                    sortBy={sort ? convertUrlSortParamToTableSortBy(sort) : undefined}
                    updateUrlParamsOnSortChange={updateUrlParamsOnSortChange}
                    fetchFirstPageWithNewSort={fetchFirstPageWithNewSort}
                    totalCount={numInvoices}
                    emptyState={
                        <SalesDocumentOverviewEmptyState
                            hasAnyFilters={hasAnyFilters}
                            type='INVOICE'
                        />
                    }
                    error={error || undefined}
                    // @ts-expect-error
                    renderRowActionMenu={quoteRowActionMenu}
                />
            </div>
            <div className={styles['quote-script-container']}>
                {/* @ts-expect-error */}
                <div id={NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID} ref={scriptRef} />
            </div>
        </div>
    );
}
